/**
 * LifeEventModal Component
 * Single Responsibility: Handle adding and editing life events
 */

import React, { useState, useEffect } from 'react'
import {
  Heart,
  Baby,
  Briefcase,
  GraduationCap,
  Activity,
  TrendingUp,
  Home,
  DollarSign,
  Circle,
  HeartCrack,
  Calendar,
  X
} from 'lucide-react'
import { LifeEvent } from '../../../types/meeting'

interface LifeEventModalProps {
  isOpen: boolean
  event: LifeEvent | null
  onClose: () => void
  onSave: (event: Omit<LifeEvent, 'id' | 'source' | 'timestamp'>) => void
}

const LifeEventModal: React.FC<LifeEventModalProps> = ({
  isOpen,
  event,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    date: '',
    type: 'other' as LifeEvent['type'],
    description: '',
    financialImpact: 'none' as LifeEvent['financialImpact']
  })

  // Event type options with icons
  const eventTypes: { value: LifeEvent['type']; label: string; icon: React.ReactNode }[] = [
    { value: 'marriage', label: 'Marriage', icon: <Heart className="w-4 h-4" /> },
    { value: 'divorce', label: 'Divorce', icon: <HeartCrack className="w-4 h-4" /> },
    { value: 'birth', label: 'Birth', icon: <Baby className="w-4 h-4" /> },
    { value: 'death', label: 'Death', icon: <Circle className="w-4 h-4" /> },
    { value: 'retirement', label: 'Retirement', icon: <Briefcase className="w-4 h-4" /> },
    { value: 'education', label: 'Education', icon: <GraduationCap className="w-4 h-4" /> },
    { value: 'health', label: 'Health', icon: <Activity className="w-4 h-4" /> },
    { value: 'career', label: 'Career', icon: <TrendingUp className="w-4 h-4" /> },
    { value: 'relocation', label: 'Relocation', icon: <Home className="w-4 h-4" /> },
    { value: 'inheritance', label: 'Inheritance', icon: <DollarSign className="w-4 h-4" /> },
    { value: 'other', label: 'Other', icon: <Calendar className="w-4 h-4" /> }
  ]

  // Financial impact options
  const financialImpacts: { value: LifeEvent['financialImpact']; label: string; color: string }[] = [
    { value: 'high', label: 'High', color: 'text-red-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'none', label: 'None', color: 'text-slate-600' }
  ]

  // Initialize form when event changes (edit mode)
  useEffect(() => {
    if (event) {
      setFormData({
        date: event.date,
        type: event.type,
        description: event.description,
        financialImpact: event.financialImpact || 'none'
      })
    } else {
      // Reset for new event
      setFormData({
        date: new Date().toISOString().split('T')[0],
        type: 'other',
        description: '',
        financialImpact: 'none'
      })
    }
  }, [event])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.description.trim()) {
      alert('Please enter a description for the life event')
      return
    }

    onSave({
      date: formData.date,
      type: formData.type,
      description: formData.description.trim(),
      financialImpact: formData.financialImpact,
      confidenceScore: event?.confidenceScore
    })

    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-kielis-navy">
            {event ? 'Edit Life Event' : 'Add New Life Event'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Event Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Event Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as LifeEvent['type'] })}
              className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
            >
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {/* Type preview */}
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
              {eventTypes.find(t => t.value === formData.type)?.icon}
              <span>{eventTypes.find(t => t.value === formData.type)?.label}</span>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Event Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
              placeholder="Enter event details..."
              rows={3}
              required
            />
          </div>

          {/* Financial Impact */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Financial Impact
            </label>
            <div className="grid grid-cols-4 gap-2">
              {financialImpacts.map((impact) => (
                <button
                  key={impact.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, financialImpact: impact.value })}
                  className={`p-2 text-sm rounded-lg border transition-all ${
                    formData.financialImpact === impact.value
                      ? 'bg-kielis-navy text-white border-kielis-navy'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {impact.label}
                </button>
              ))}
            </div>
          </div>

          {/* Confidence Score (if editing auto-detected event) */}
          {event?.source === 'transcription' && event.confidenceScore && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                This event was auto-detected with {Math.round(event.confidenceScore * 100)}% confidence
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-kielis-navy text-white rounded-lg hover:bg-slate-800 transition-all"
            >
              {event ? 'Update Event' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LifeEventModal